/**
 * bibliothequeCatalog.js
 * ----------------------------------------------------------------
 * Source unique de vérité pour la "Bibliothèque" — accessible à la
 * fois côté élève et côté enseignant.
 *
 * Chaque catégorie possède :
 *  - metadata (titre, sous-titre, accent couleur, image de fond, icône)
 *  - liste ordonnée de cours cliquables
 *
 * Utilisée par : /pages/Bibliotheque.jsx (nouveau hub unifié)
 * ---------------------------------------------------------------- */
import {
  Bike, Compass, Sparkles, GraduationCap, Radio,
} from 'lucide-react';

/**
 * Catalogue complet — 5 catégories principales.
 * L'ordre reflète le parcours pédagogique naturel : AM → Plateau → Circulation → 125 → Briefings.
 */
export const BIBLIOTHEQUE_CATALOG = [
  {
    key: 'permis-am',
    slug: 'permis-am',
    icon: Sparkles,
    title: 'Permis AM',
    subtitle: 'Dès 14 ans · Cyclomoteur 50 cm³',
    tagline: 'Ma toute première formation à la conduite motorisée',
    accent: 'from-amber-500/40 via-orange-500/20 to-transparent',
    ring: 'ring-amber-500/40',
    dot: 'bg-amber-500',
    badge: 'text-amber-300 border-amber-500/40 bg-amber-500/15',
    // Image de fond disponible dans /public/images (fallback : gradient si absente)
    cover: '/images/cover-permis-am.jpg',
    coverFallback: 'linear-gradient(135deg, #b45309, #92400e)',
    courses: [
      { path: '/cours/permis-am-sensibilisation', label: 'Cérémonie S5 · Sensibilisation immersive', duration: '45 min', badge: 'STAR', preview: true },
      { path: '/cours/permis-am-journee-1', label: 'Journée 1 · Théorie & plateau', duration: '4 h' },
      { path: '/cours/permis-am-journee-2', label: 'Journée 2 · Circulation supervisée', duration: '3 h' },
      { path: '/permis-am/programme', label: 'Programme officiel Rousseau', duration: 'Ressource', publicAccess: true },
    ],
  },
  {
    key: 'plateau-a2',
    slug: 'plateau',
    icon: Compass,
    title: 'Plateau A2',
    subtitle: 'Maîtrise du véhicule · 6 manœuvres officielles',
    tagline: 'Freinage, lenteur, évitement, slalom — les fondamentaux',
    accent: 'from-rose-500/40 via-red-500/20 to-transparent',
    ring: 'ring-rose-500/40',
    dot: 'bg-rose-500',
    badge: 'text-rose-300 border-rose-500/40 bg-rose-500/15',
    cover: '/images/cover-plateau.jpg',
    coverFallback: 'linear-gradient(135deg, #be123c, #881337)',
    courses: [
      { path: '/cours/premiere-plateau-a2', label: '1ʳᵉ leçon · Prise en main', duration: '2 h', preview: true },
      { path: '/cours/deuxieme-plateau-a2', label: '2ᵉ leçon · Maîtrise allure réduite', duration: '2 h' },
      { path: '/cours/troisieme-plateau-a2', label: '3ᵉ leçon · Freinage & évitement', duration: '2 h' },
      { path: '/cours/quatrieme-plateau-a2', label: '4ᵉ leçon · Passage examen', duration: '2 h' },
      { path: '/livret-plateau', label: 'Livret plateau · Progression élève', duration: 'Interactif' },
    ],
  },
  {
    key: 'circulation-a2',
    slug: 'circulation',
    icon: Bike,
    title: 'Circulation A2',
    subtitle: '6 leçons · De la ville à l\'autoroute',
    tagline: 'Observer, anticiper, adapter — devenir autonome',
    accent: 'from-blue-500/40 via-cyan-500/20 to-transparent',
    ring: 'ring-blue-500/40',
    dot: 'bg-blue-500',
    badge: 'text-blue-300 border-blue-500/40 bg-blue-500/15',
    cover: '/images/cover-circulation.jpg',
    coverFallback: 'linear-gradient(135deg, #1d4ed8, #1e3a8a)',
    courses: [
      { path: '/cours/premiere-circulation-a2', label: '1ʳᵉ circulation · Découverte trafic', duration: '2 h', preview: true },
      { path: '/cours/deuxieme-circulation-a2', label: '2ᵉ circulation · Intersections & priorités', duration: '2 h' },
      { path: '/cours/troisieme-circulation-a2', label: '3ᵉ circulation · Route & vitesse', duration: '2 h' },
      { path: '/cours/quatrieme-circulation-a2', label: '4ᵉ circulation · Voie rapide & autoroute', duration: '2 h' },
      { path: '/cours/cinquieme-circulation-a2', label: '5ᵉ circulation · Nuit & conditions dégradées', duration: '2 h' },
      { path: '/cours/sixieme-circulation-a2', label: '6ᵉ circulation · Bilan & examen blanc', duration: '2 h' },
      { path: '/livret-circulation', label: 'Livret circulation · Progression élève', duration: 'Interactif' },
    ],
  },
  {
    key: 'formation-125',
    slug: '125',
    icon: GraduationCap,
    title: 'Formation 125',
    subtitle: '7 heures obligatoires · Passerelle B → 125 cm³',
    tagline: 'Comprendre, maîtriser, anticiper',
    accent: 'from-emerald-500/40 via-teal-500/20 to-transparent',
    ring: 'ring-emerald-500/40',
    dot: 'bg-emerald-500',
    badge: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/15',
    cover: '/images/cover-125.jpg',
    coverFallback: 'linear-gradient(135deg, #059669, #064e3b)',
    courses: [
      { path: '/formation-125/comprendre', label: 'Comprendre · Théorie', duration: '2 h', preview: true },
      { path: '/formation-125/maitriser', label: 'Maîtriser · Plateau', duration: '2 h' },
      { path: '/formation-125/anticiper', label: 'Anticiper · Circulation', duration: '3 h' },
    ],
  },
  {
    key: 'briefings',
    slug: 'briefings',
    icon: Radio,
    title: 'Briefings terrain',
    subtitle: 'Mobile-first · À projeter avant la sortie moto',
    tagline: 'Le rituel pré-ride qui sauve des vies',
    accent: 'from-purple-500/40 via-fuchsia-500/20 to-transparent',
    ring: 'ring-purple-500/40',
    dot: 'bg-purple-500',
    badge: 'text-purple-300 border-purple-500/40 bg-purple-500/15',
    cover: '/images/cover-briefings.jpg',
    coverFallback: 'linear-gradient(135deg, #7c3aed, #5b21b6)',
    courses: [
      { path: '/briefing-a2', label: 'Briefing circulation A2', duration: '5 min' },
      { path: '/briefing-circulation', label: 'Briefing séance circulation AM', duration: '5 min' },
    ],
  },
];

/** Retourne la catégorie correspondant au `slug` d'URL. */
export const findCategoryBySlug = (slug) =>
  BIBLIOTHEQUE_CATALOG.find((c) => c.slug === slug) || null;

/** Nombre total de contenus dans le catalogue (utile pour les compteurs UI). */
export const TOTAL_CONTENUS = BIBLIOTHEQUE_CATALOG.reduce(
  (sum, cat) => sum + cat.courses.length,
  0,
);
